import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { Users, Shield, Trash2, Search, Plus, Bell, Heart, MessageSquare, Edit3, LogOut, ChevronDown, Flag, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { getReports, updateReport } from '../lib/api-client';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { PixelatedTurtle } from '@theme/design-constants';
import LoadingScreen from '../components/desktop/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { getCachedTheme } from '../utils/themeCache';

import { fetchWithAuth } from '../lib/fetchWithAuth';
type User = {
  id: number;
  username: string;
  profileName: string | null;
  profilePicture: string | null;
  isModerator: boolean;
  isAdmin: boolean;
  isSuperAdmin?: boolean;
};

type Community = {
  id: string;
  name: string;
  nameSlug: string;
  description: string | null;
  avatar: string | null;
  memberCount: number;
  postCount: number;
};

export default function Moderation() {
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { user: authUser, logout, loading: authLoading } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const [loading, setLoading] = useState(true);
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const scale = useScale();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Sync animations when siteAnimations or currentPreset changes
  useEffect(() => {
    if (siteAnimations) {
      setThemeAnimations(siteAnimations);
    } else {
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [siteAnimations, currentPreset]);

  // Register site theme with NavigationContext - ONLY when theme fully loads
  // CRITICAL: Do NOT register on mount, or it will overwrite the previous page's theme
  // before LoadingScreen can read it
  useEffect(() => {
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      // Delay registration by 100ms to ensure LoadingScreen reads the previous page's theme
      const timer = setTimeout(() => {
        console.log('[Moderation] 🎨 Setting currentTheme for loading screen (delayed):');
        setCurrentTheme({
          type: 'site',
          colors: {
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor || theme.primaryColor,
            textColor: theme.textColor || '#ffffff',
            accentColor: theme.accentColor || theme.primaryColor
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);

  // User management
  const [users, setUsers] = useState<User[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  // Community management
  const [communities, setCommunities] = useState<Community[]>([]);
  const [communitySearchQuery, setCommunitySearchQuery] = useState('');
  const [filteredCommunities, setFilteredCommunities] = useState<Community[]>([]);

  // Create community modal
  const [showCreateCommunityModal, setShowCreateCommunityModal] = useState(false);
  const [newCommunityName, setNewCommunityName] = useState('');
  const [newCommunityDescription, setNewCommunityDescription] = useState('');
  const [newCommunityAccessType, setNewCommunityAccessType] = useState<'public' | 'request' | 'invite_only' | 'private'>('public');
  const [isCreatingCommunity, setIsCreatingCommunity] = useState(false);

  // Reports
  const [reports, setReports] = useState<any[]>([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportStatusFilter, setReportStatusFilter] = useState('pending');
  const [reportsLoading, setReportsLoading] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const navItems = [
    { label: 'Home', path: '/feed' },
    { label: 'Friends', path: '/friends' },
    { label: 'Messages', path: '/messages' },
    { label: 'Community', path: '/community' },
    { label: 'Ads', path: '/ads' },
    { label: 'Marketplace', path: '/marketplace' }
  ];

  useEffect(() => {
    async function loadData() {
      if (authLoading) return;

      try {
        // Check if user is moderator or admin
        if (!authUser || (!authUser.isSiteModerator && !authUser.isSiteAdmin)) {
          navigate('/feed');
          return;
        }

        // Load users (admin only)
        if (authUser.isSiteAdmin) {
          try {
            const usersResponse = await fetchWithAuth('/api/moderation/users', {
              credentials: 'include'
            });
            if (usersResponse.ok) {
              const data = await usersResponse.json();
              setUsers(data.users || []);
              setFilteredUsers(data.users || []);
            }
          } catch (e) {
          }

          // Load communities (admin only)
          try {
            const communitiesResponse = await fetchWithAuth('/api/moderation/communities', {
              credentials: 'include'
            });
            if (communitiesResponse.ok) {
              const data = await communitiesResponse.json();
              setCommunities(data.communities || []);
              setFilteredCommunities(data.communities || []);
            }
          } catch (e) {
          }
        }

        // Load reports (all moderators and admins)
        try {
          const reportsData = await getReports({ status: 'pending', limit: 50 });
          setReports(reportsData.reports || []);
          setReportsTotal(reportsData.total || 0);
        } catch (e) {
        }
      } catch (error) {
        navigate('/feed');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [authUser, authLoading, navigate]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  useEffect(() => {
    if (userSearchQuery.trim()) {
      setFilteredUsers(
        users.filter(u =>
          u.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
          u.profileName?.toLowerCase().includes(userSearchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredUsers(users);
    }
  }, [userSearchQuery, users]);

  useEffect(() => {
    if (communitySearchQuery.trim()) {
      setFilteredCommunities(
        communities.filter(c =>
          c.name.toLowerCase().includes(communitySearchQuery.toLowerCase()) ||
          c.description?.toLowerCase().includes(communitySearchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredCommunities(communities);
    }
  }, [communitySearchQuery, communities]);

  const handleToggleModerator = async (userId: number, currentStatus: boolean) => {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'remove moderator status from' : 'make'} this user ${currentStatus ? 'a regular user' : 'a moderator'}?`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/moderation/users/${userId}/moderator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isModerator: !currentStatus })
      });

      if (response.ok) {
        setUsers(prev =>
          prev.map(u =>
            u.id === userId ? { ...u, isModerator: !currentStatus } : u
          )
        );
        alert(`User ${currentStatus ? 'removed from moderators' : 'promoted to moderator'} successfully!`);
      } else {
        alert('Failed to update user status');
      }
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const handleToggleAdmin = async (userId: number, currentStatus: boolean) => {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'remove admin status from' : 'make'} this user ${currentStatus ? 'a regular user' : 'an admin'}?`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/moderation/users/${userId}/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isAdmin: !currentStatus })
      });

      if (response.ok) {
        setUsers(prev =>
          prev.map(u =>
            u.id === userId ? { ...u, isAdmin: !currentStatus } : u
          )
        );
        alert(`User ${currentStatus ? 'removed from admins' : 'promoted to admin'} successfully!`);
      } else {
        alert('Failed to update user status');
      }
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const handleDeleteCommunity = async (communityId: string, communityName: string) => {
    if (!confirm(`Are you sure you want to DELETE the community "${communityName}"? This action cannot be undone!`)) {
      return;
    }

    const confirmText = prompt(`Type "${communityName}" to confirm deletion:`);
    if (confirmText !== communityName) {
      alert('Community name did not match. Deletion cancelled.');
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/moderation/communities/${communityId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setCommunities(prev => prev.filter(c => c.id !== communityId));
        alert('Community deleted successfully!');
      } else {
        alert('Failed to delete community');
      }
    } catch (error) {
      alert('Failed to delete community');
    }
  };

  const handleCreateCommunity = async () => {
    if (!newCommunityName.trim()) {
      alert('Community name is required');
      return;
    }

    setIsCreatingCommunity(true);
    try {
      const response = await fetchWithAuth('/api/communities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newCommunityName,
          description: newCommunityDescription,
          accessType: newCommunityAccessType
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert('Community created successfully!');
        setShowCreateCommunityModal(false);
        setNewCommunityName('');
        setNewCommunityDescription('');
        setNewCommunityAccessType('public');
        navigate(`/communities/${data.community.nameSlug}`);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create community');
      }
    } catch (error) {
      alert('Failed to create community');
    } finally {
      setIsCreatingCommunity(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: theme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: theme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: theme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: theme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: theme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: theme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
        if (notification.postId) {
          navigate(`/posts/${notification.postId}`);
        }
        break;
      case 'COMMUNITY_REPLY':
        if (notification.communityId && notification.postId) {
          navigate(`/community/${notification.communityId}/posts/${notification.postId}`);
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
    }
  };

  const handleLogout = async () => {
    logout();
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden" style={{ background: theme.appBg, color: theme.textColor }}>
      <AnimationLayer
        animations={themeAnimations}
        isMobile={false}
        bubbleColor={theme.accentColor}
      />

      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 w-full border-b" style={{
        backgroundColor: theme.navbarBg || 'rgba(6, 182, 212, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(10px)'
      }}>
        <div className="w-full flex items-center justify-center" style={{
          paddingLeft: `${scaleW(32, scale.w)}px`,
          paddingRight: `${scaleW(32, scale.w)}px`,
          height: `${scaleH(80, scale.h)}px`
        }}>
          <div className="w-full flex items-center justify-between" style={{
            paddingLeft: `${scaleW(80, scale.w)}px`,
            paddingRight: `${scaleW(48, scale.w)}px`,
            gap: `${scaleW(32, scale.w)}px`
          }}>
            {/* Left Side - EarthSocial Title */}
            <div className="flex-shrink-0">
              <h1 className="font-bold" style={{
                color: theme.textColor,
                fontSize: `${scaleW(48, scale.w)}px`
              }}>
                Earth<span style={{ color: '#E91E63' }}>Social</span>
              </h1>
            </div>

            {/* Navigation Items - maximized and evenly spaced */}
            <div className="flex items-center justify-between flex-1" style={{ gap: `${scaleW(24, scale.w)}px` }}>
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className="flex-1 font-semibold rounded-lg border-2 transition-opacity duration-300 hover:opacity-90 whitespace-nowrap"
                  style={{
                    paddingLeft: `${scaleW(14, scale.w)}px`,
                    paddingRight: `${scaleW(14, scale.w)}px`,
                    paddingTop: `${scaleH(7, scale.h)}px`,
                    paddingBottom: `${scaleH(7, scale.h)}px`,
                    fontSize: `${scaleW(28, scale.w)}px`,
                    backgroundColor: item.path === '/ads' ? theme.accentColor : 'rgba(255, 255, 255, 0.1)',
                    color: theme.textColor,
                    borderColor: item.path === '/ads' ? theme.accentColor : 'rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Notifications Bell */}
            <div className="relative flex-shrink-0" style={{ marginTop: '0.1rem' }}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 transition-all hover:opacity-80"
                style={{
                  color: theme.textColor
                }}
              >
                <Bell size={Math.round(scaleW(32, scale.w))} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 rounded-full text-xs font-bold px-1.5 py-0.5"
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: `${scaleW(12, scale.w)}px`,
                      minWidth: `${scaleW(20, scale.w)}px`,
                      textAlign: 'center'
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <>
                  <div
                    className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl backdrop-blur-sm"
                    style={{
                      backgroundColor: theme.cardBg.replace(/[\d.]+\)$/, '0.98)'),
                      borderColor: theme.accentColor + '40',
                      border: '1px solid',
                      width: `${scaleW(400, scale.w)}px`,
                      maxHeight: `${scaleH(500, scale.h)}px`,
                      zIndex: 1001
                    }}
                  >
                    {/* Header */}
                    <div
                      className="flex items-center justify-center p-4 border-b relative"
                      style={{
                        borderColor: theme.accentColor + '40',
                        backgroundColor: theme.tabBarBg || 'rgba(30, 58, 95, 0.95)'
                      }}
                    >
                      <h3 className="font-bold text-lg" style={{ color: theme.textColor }}>
                        Notifications
                      </h3>
                      {unreadCount > 0 && (
                        <button
                          className="absolute right-4 text-sm hover:opacity-80 transition-opacity"
                          onClick={async () => {
                            try {
                              await fetchWithAuth('/api/notifications/read-all', {
                                method: 'POST',
                                credentials: 'include'
                              });
                              setUnreadCount(0);
                              setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                            } catch (error) {
                            }
                          }}
                          style={{ color: theme.accentColor }}
                        >
                          Mark all read
                        </button>
                      )}
                    </div>

                    {/* Notifications List */}
                    <div className="overflow-y-auto" style={{ maxHeight: `${scaleH(440, scale.h)}px` }}>
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12" style={{ color: theme.textColor + '99' }}>
                          <Bell size={48} style={{ color: theme.textColor + '40' }} className="mb-3" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notification) => {
                          const notifIcon = getNotificationIcon(notification.type);
                          const notifMessage = getNotificationMessage(notification);

                          return (
                            <div
                              key={notification.id}
                              onClick={async () => {
                                if (!notification.read) {
                                  try {
                                    await fetchWithAuth(`/api/notifications/${notification.id}/read`, {
                                      method: 'POST',
                                      credentials: 'include'
                                    });
                                    setNotifications(prev =>
                                      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
                                    );
                                    setUnreadCount(prev => Math.max(0, prev - 1));
                                  } catch (error) {
                                  }
                                }
                                handleNotificationClick(notification);
                              }}
                              className="p-4 border-b cursor-pointer transition-all hover:opacity-80"
                              style={{
                                backgroundColor: notification.read ? 'transparent' : theme.accentColor + '10',
                                borderColor: theme.accentColor + '20'
                              }}
                            >
                              <div className="flex items-start gap-3">
                                {notification.actorAvatar ? (
                                  <img
                                    src={notification.actorAvatar}
                                    alt={notification.actorName || 'User'}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: theme.accentColor + '40' }}
                                  >
                                    {notifIcon}
                                  </div>
                                )}

                                <div className="flex-1 min-w-0">
                                  <p className="text-sm" style={{ color: theme.textColor }}>
                                    {notifMessage}
                                  </p>
                                  <p className="text-xs mt-1" style={{ color: theme.textColor + '66' }}>
                                    {formatNotificationTime(notification.createdAt)}
                                  </p>
                                </div>

                                {!notification.read && (
                                  <div
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: theme.accentColor }}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 1000 }}
                    onClick={() => setShowNotifications(false)}
                  />
                </>
              )}
            </div>

            {/* Right Side - Profile/Logo (maximized, no border) */}
            <div className="flex flex-col items-center flex-shrink-0 relative" style={{ marginBottom: `${scaleH(-8, scale.h)}px` }}>
              {/* Pixelated Turtle - Links to Profile */}
              <Link to="/u" className="hover:opacity-80 transition-opacity">
                <PixelatedTurtle size={scaleW(100, scale.w)} />
              </Link>

              {/* Dropdown Toggle Button - Centered below turtle */}
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="hover:opacity-80 transition-opacity mt-1"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <ChevronDown size={20} style={{ color: theme.textColor }} />
              </button>

              {/* Profile Dropdown Menu */}
              {showProfileMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 999 }}
                    onClick={() => setShowProfileMenu(false)}
                  />
                  {/* Menu */}
                  <div
                    className="absolute top-full right-0 mt-2 rounded-lg shadow-lg border"
                    style={{
                      background: theme.cardBg,
                      borderColor: theme.accentColor + '40',
                      minWidth: '200px',
                      zIndex: 1000
                    }}
                  >
                    <Link
                      to="/u"
                      className="flex items-center gap-3 px-4 py-3 hover:opacity-80 transition-opacity border-b"
                      style={{ color: theme.textColor, borderColor: theme.accentColor + '20' }}
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <Users size={20} />
                      <span>My Profile</span>
                    </Link>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:opacity-80 transition-opacity"
                      style={{ color: theme.textColor }}
                    >
                      <LogOut size={20} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Page Header */}
      <div className="pt-6 pb-4">
        <h1 className="text-4xl font-bold text-center" style={{ color: theme.textColor }}>Moderation</h1>
      </div>

      {/* Main Layout */}
      <div className="mx-auto pb-8 w-full">
        {/* Centered Container for Widgets */}
        <div className="flex justify-center gap-6 px-8 pt-6">
          {/* User Management Widget */}
          {(authUser?.isSiteAdmin || authUser?.isSuperAdmin) && (
            <div style={{ width: `${scaleW(450, scale.w)}px` }}>
              <div className="rounded-lg p-5 backdrop-blur-sm border" style={{
                background: theme.cardBg,
                borderColor: theme.accentColor + '40',
                minHeight: `${scaleH(400, scale.h)}px`
              }}>
                <h3 className="text-lg font-bold mb-4 text-center" style={{ color: theme.textColor }}>User Management</h3>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="w-full py-2 rounded-lg mb-4 outline-none transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: theme.textColor,
                    borderColor: theme.accentColor + '40',
                    border: '1px solid',
                    paddingLeft: '0.75rem',
                    paddingRight: '1rem'
                  }}
                />

                {/* User List */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: `1px solid ${theme.accentColor}20`
                      }}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        {user.profilePicture ? (
                          <img
                            src={user.profilePicture}
                            alt={user.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: theme.accentColor + '40' }}
                          >
                            <Users size={20} style={{ color: theme.textColor }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{user.profileName || user.username}</div>
                          <div className="text-xs opacity-60 truncate">@{user.username}</div>
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {user.isSuperAdmin && (
                          <div className="text-xs px-2 py-1 rounded" style={{
                            backgroundColor: '#ef4444',
                            color: 'white'
                          }}>
                            Super Admin
                          </div>
                        )}
                        {user.isAdmin && !user.isSuperAdmin && (
                          <div className="text-xs px-2 py-1 rounded" style={{
                            backgroundColor: theme.accentColor + '40',
                            color: theme.textColor
                          }}>
                            Admin
                          </div>
                        )}
                        {user.isModerator && !user.isAdmin && (
                          <div className="text-xs px-2 py-1 rounded" style={{
                            backgroundColor: theme.accentColor + '20',
                            color: theme.textColor
                          }}>
                            Moderator
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        {authUser?.isSuperAdmin && !user.isSuperAdmin && (
                          <button
                            onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                            className="w-full px-3 py-2 rounded-lg transition-all hover:opacity-80 text-sm"
                            style={{
                              backgroundColor: user.isAdmin ? theme.accentColor + '40' : theme.accentColor,
                              color: theme.textColor
                            }}
                          >
                            {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                          </button>
                        )}
                        {!user.isAdmin && !user.isSuperAdmin && (
                          <button
                            onClick={() => handleToggleModerator(user.id, user.isModerator)}
                            className="w-full px-3 py-2 rounded-lg transition-all hover:opacity-80 text-sm"
                            style={{
                              backgroundColor: user.isModerator ? theme.accentColor + '40' : theme.accentColor,
                              color: theme.textColor
                            }}
                          >
                            {user.isModerator ? 'Remove Moderator' : 'Make Moderator'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8" style={{ color: theme.textColor + '99' }}>
                      <Users size={48} className="mx-auto mb-3" style={{ color: theme.textColor + '40' }} />
                      <p className="text-sm">No users found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Community Management Widget */}
          {authUser?.isSiteAdmin && (
            <div style={{ width: `${scaleW(450, scale.w)}px` }}>
              <div className="rounded-lg p-5 backdrop-blur-sm border" style={{
                background: theme.cardBg,
                borderColor: theme.accentColor + '40',
                minHeight: `${scaleH(400, scale.h)}px`
              }}>
                <h3 className="text-lg font-bold mb-4 text-center" style={{ color: theme.textColor }}>Community Management</h3>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Search communities..."
                  value={communitySearchQuery}
                  onChange={(e) => setCommunitySearchQuery(e.target.value)}
                  className="w-full py-2 rounded-lg mb-4 outline-none transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: theme.textColor,
                    borderColor: theme.accentColor + '40',
                    border: '1px solid',
                    paddingLeft: '0.75rem',
                    paddingRight: '1rem'
                  }}
                />

                {/* Community List */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredCommunities.map((community) => (
                    <div
                      key={community.id}
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: `1px solid ${theme.accentColor}20`
                      }}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        {community.avatar ? (
                          <img
                            src={community.avatar}
                            alt={community.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: theme.accentColor + '40' }}
                          >
                            <Users size={20} style={{ color: theme.textColor }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{community.name}</div>
                          {community.description && (
                            <div className="text-xs opacity-60 line-clamp-1">{community.description}</div>
                          )}
                        </div>
                      </div>

                      <div className="text-xs opacity-60 mb-2">
                        {community.memberCount} members • {community.postCount} posts
                      </div>

                      <button
                        onClick={() => handleDeleteCommunity(community.id, community.name)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all hover:opacity-80 text-sm"
                        style={{
                          backgroundColor: '#ef4444',
                          color: 'white'
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  ))}
                  {filteredCommunities.length === 0 && (
                    <div className="text-center py-8" style={{ color: theme.textColor + '99' }}>
                      <Users size={48} className="mx-auto mb-3" style={{ color: theme.textColor + '40' }} />
                      <p className="text-sm">No communities found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reports Widget */}
          <div style={{ width: `${scaleW(550, scale.w)}px` }}>
            <div className="rounded-lg p-5 backdrop-blur-sm border" style={{
              background: theme.cardBg,
              borderColor: theme.accentColor + '40',
              minHeight: `${scaleH(400, scale.h)}px`
            }}>
              <div className="flex items-center gap-2 mb-4 justify-center">
                <Flag size={20} style={{ color: '#f59e0b' }} />
                <h3 className="text-lg font-bold" style={{ color: theme.textColor }}>
                  Reports {reportsTotal > 0 && <span className="text-sm font-normal opacity-60">({reportsTotal})</span>}
                </h3>
              </div>

              {/* Status filter */}
              <div className="flex gap-2 mb-4">
                {['pending', 'reviewed', 'dismissed', 'actioned'].map((status) => (
                  <button
                    key={status}
                    onClick={async () => {
                      setReportStatusFilter(status);
                      setReportsLoading(true);
                      try {
                        const data = await getReports({ status, limit: 50 });
                        setReports(data.reports || []);
                        setReportsTotal(data.total || 0);
                      } catch { /* silent */ }
                      finally { setReportsLoading(false); }
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{
                      backgroundColor: reportStatusFilter === status ? theme.accentColor : 'rgba(255,255,255,0.08)',
                      color: reportStatusFilter === status ? '#fff' : theme.textColor,
                      border: `1px solid ${reportStatusFilter === status ? theme.accentColor : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>

              {/* Reports list */}
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {reportsLoading && (
                  <div className="text-center py-8" style={{ color: theme.textColor + '99' }}>
                    <p className="text-sm">Loading reports...</p>
                  </div>
                )}

                {!reportsLoading && reports.length === 0 && (
                  <div className="text-center py-8" style={{ color: theme.textColor + '99' }}>
                    <Flag size={48} className="mx-auto mb-3" style={{ color: theme.textColor + '40' }} />
                    <p className="text-sm">No {reportStatusFilter} reports</p>
                  </div>
                )}

                {!reportsLoading && reports.map((report) => {
                  const post = report.post || report.communityPost;
                  const authorUsername = post?.author?.username || 'Unknown';
                  const communityName = report.communityPost?.community?.name;
                  const contentPreview = post?.content
                    ? (post.content.length > 120 ? post.content.slice(0, 120) + '...' : post.content)
                    : 'Content unavailable';

                  return (
                    <div
                      key={report.id}
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: `1px solid ${theme.accentColor}20`,
                      }}
                    >
                      {/* Reporter info */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs" style={{ color: theme.textColor, opacity: 0.6 }}>
                          Reported by <span className="font-semibold">{report.reporter?.username || 'Unknown'}</span>
                        </div>
                        <div className="text-xs" style={{ color: theme.textColor, opacity: 0.4 }}>
                          {new Date(report.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Reason */}
                      <div
                        className="text-sm font-medium mb-2 px-2 py-1 rounded"
                        style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}
                      >
                        {report.reason}
                      </div>

                      {/* Post preview */}
                      <div
                        className="text-xs mb-2 p-2 rounded"
                        style={{ backgroundColor: 'rgba(255,255,255,0.03)', color: theme.textColor, opacity: 0.7 }}
                      >
                        <div className="font-semibold mb-1">
                          @{authorUsername}
                          {communityName && <span className="opacity-60"> in {communityName}</span>}
                        </div>
                        {post?.title && <div className="font-semibold">{post.title}</div>}
                        <div>{contentPreview}</div>
                      </div>

                      {/* Actions */}
                      {report.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              try {
                                await updateReport(report.id, { status: 'dismissed' });
                                setReports(prev => prev.filter(r => r.id !== report.id));
                                setReportsTotal(prev => Math.max(0, prev - 1));
                              } catch { /* silent */ }
                            }}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                            style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: theme.textColor }}
                          >
                            <XCircle size={14} />
                            Dismiss
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await updateReport(report.id, { status: 'actioned', reviewNote: 'Action taken by moderator' });
                                setReports(prev => prev.filter(r => r.id !== report.id));
                                setReportsTotal(prev => Math.max(0, prev - 1));
                              } catch { /* silent */ }
                            }}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                            style={{ backgroundColor: '#ef4444', color: '#fff' }}
                          >
                            <AlertTriangle size={14} />
                            Take Action
                          </button>
                        </div>
                      )}

                      {/* Review info for non-pending */}
                      {report.status !== 'pending' && report.reviewer && (
                        <div className="text-xs mt-1" style={{ color: theme.textColor, opacity: 0.4 }}>
                          {report.status === 'dismissed' ? 'Dismissed' : 'Actioned'} by {report.reviewer.username}
                          {report.reviewNote && ` — ${report.reviewNote}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Moderator-only message */}
          {authUser?.isSiteModerator && !authUser?.isSiteAdmin && (
            <div style={{ width: `${scaleW(450, scale.w)}px` }}>
              <div className="rounded-lg p-5 backdrop-blur-sm border" style={{
                background: theme.cardBg,
                borderColor: theme.accentColor + '40',
                minHeight: `${scaleH(400, scale.h)}px`
              }}>
                <h3 className="text-lg font-bold mb-2 text-center" style={{ color: theme.textColor }}>Moderator Access</h3>
                <p className="text-sm opacity-80 text-center">
                  Use the Create Community button to create new communities.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Community Button - Fixed Top Left */}
      <button
        onClick={() => setShowCreateCommunityModal(true)}
        className="fixed top-24 left-6 rounded-lg shadow-lg font-semibold overflow-hidden flex items-center gap-2"
        style={{
          backgroundColor: theme.accentColor,
          color: theme.textColor,
          zIndex: 1000,
          padding: '0.75rem 2rem',
          fontSize: '1.125rem',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: showCreateCommunityModal ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        <Plus size={20} />
        Create Community
      </button>

      {/* Create Community Modal */}
      {showCreateCommunityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
          <div className="rounded-2xl backdrop-blur-sm border p-6 max-w-md w-full" style={{
            backgroundColor: theme.cardBg,
            borderColor: theme.accentColor + '40'
          }}>
            <h2 className="text-2xl font-bold mb-4">Create Community</h2>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium">Community Name</label>
                <input
                  type="text"
                  value={newCommunityName}
                  onChange={(e) => setNewCommunityName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg outline-none"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: theme.textColor,
                    border: `1px solid ${theme.accentColor}40`
                  }}
                  placeholder="Enter community name"
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium">Description</label>
                <textarea
                  value={newCommunityDescription}
                  onChange={(e) => setNewCommunityDescription(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg outline-none resize-none"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: theme.textColor,
                    border: `1px solid ${theme.accentColor}40`
                  }}
                  placeholder="Enter community description"
                  rows={3}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium">Access Type</label>
                <select
                  value={newCommunityAccessType}
                  onChange={(e) => setNewCommunityAccessType(e.target.value as any)}
                  className="w-full px-4 py-2 rounded-lg outline-none"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: theme.textColor,
                    border: `1px solid ${theme.accentColor}40`
                  }}
                >
                  <option value="public">Public</option>
                  <option value="request">Request to Join</option>
                  <option value="invite_only">Invite Only</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCreateCommunity}
                  disabled={isCreatingCommunity}
                  className="flex-1 py-2 rounded-lg transition-all hover:opacity-80 disabled:opacity-50"
                  style={{
                    backgroundColor: theme.accentColor,
                    color: theme.textColor
                  }}
                >
                  {isCreatingCommunity ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateCommunityModal(false);
                    setNewCommunityName('');
                    setNewCommunityDescription('');
                    setNewCommunityAccessType('public');
                  }}
                  className="flex-1 py-2 rounded-lg transition-all hover:opacity-80"
                  style={{
                    backgroundColor: theme.accentColor + '40',
                    color: theme.textColor
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
